/** A6.2 registrar course words. Academic facts and provider evidence keep their owners. */
export const registrarCourseCopy = {
  legacyCopy: "An unfinished copy from the previous editor is kept for this section. Resume it to review its changes, or discard that previous copy and keep this tab's work. Nothing is saved by this choice.",
  resumeLegacy: "Resume previous editor copy",
  discardLegacy: "Discard previous editor copy",
  legacyStorageWarning: "Browser storage could not keep or remove the previous copy. The visible values are kept here; the previous copy has not been silently replaced.",
  publication: "Publication",
  teachingPublicationJob: "Read whether learners can open this course and who publishes it.",
  teachingPublication: {
    draft: { state: "Course not published", next: "Next: Administration publishes this course. Learners cannot read its published teaching content until then; preparation can continue now." },
    published: { state: "Course published", next: "Enrolled learners can read its published teaching content." },
    archived: { state: "Course archived", next: "Administration archived this course. New teaching and learner work has stopped." },
  },
  publicationJob: "Review this section's state and confirm its publication, archive or discard separately.",
  preparationNotes: "Preparation notes - these do not block publication",
  emptyRosterWarning: "No learners are enrolled yet. The registrar can add them before or after publication.",
  noSessionsWarning: (teacher?: string) => `No dated sessions are prepared. ${teacher ?? "The teaching team"} can prepare them if this course has scheduled meetings. An asynchronous course needs none.`,
  noContentWarning: (teacher?: string) => `No teaching content is published yet. ${teacher ?? "The teaching team"} can prepare and publish plans, materials and coursework separately.`,
  draftTeaching: "Assigned instructors with active teaching access can prepare plans, materials and coursework now, before learners can open this draft. The registrar still owns its institutional details and course publication.",
  reviewPublication: "Review publication",
  publicationSummary: "Review this section's publication and who can open it.",
  archiveSummary: "Move this section out of active lists while retaining its academic record.",
  discardSummary: "Review whether this synthetic test section can be removed.",
  publish: "Publish section",
  archive: "Archive section",
  discard: "Discard test section",
  courses: "Courses",
  status: { draft: "Draft", published: "Published", archived: "Archived" },
  review: "Review",
  reading: "Reading",
  reviewStale: "Could not refresh the consequences. A later change is not shown.",
  reviewFailed: "The section's consequences could not be read.",
  reviewAgain: "Read the consequences for the latest section version before confirming.",
  readReview: "Read consequences",
  repair: "Open the box that repairs this",
  onlyDraft: "Only a draft section in an open term can be published.",
  discardUnavailable: "The discard check must confirm that this section is synthetic test data.",
  keepAsRecord: "This section cannot be discarded. Archive it to retain its academic record outside active course lists.",
  openArchive: "Review record-only archive",
  discardConfirm: "Type this section's code and give a reason of at least 10 characters.",
  confirmCode: "Section code confirmation",
  discardReason: "Reason for discarding",
  whenLifecycle: "When you confirm this section change",
  publishEffect: (code: string, learners?: number, notices?: number) => learners === undefined || notices === undefined ? "Read the publication consequences before confirming." : `Publishes ${code} and issues its CRN. ${learners} active enrollments remain; ${notices} credit enrollments are checked for their initial notice.`,
  archiveEffect: (code: string, learners?: number, sessions?: number) => learners === undefined || sessions === undefined ? "Read the archive consequences before confirming." : `Archives ${code}. Its ${learners} active enrollments and ${sessions} dated sessions remain in the academic record.`,
  publicationBoundary: "Publication opens this section to linked active enrolled learners. Teaching plans, materials and coursework keep their own access rules. No provider course, meeting or personal file is created. Dated sessions are prepared separately. Notice acceptance does not prove inbox delivery.",
  archiveBoundary: "The CRN, enrollments, submitted work and academic record remain. This section leaves active course directories. New teaching and learner work stops; authorized record corrections and grading remain available. Unsubmitted private drafts without a legal hold are erased and cannot be restored; associated draft uploads are queued for deletion. Accepted email cannot be recalled.",
  discardEffect: "Removes this synthetic section and its preparatory teaching assignments, draft units and unpublished unplaced materials. An immutable audit receipt remains.",
  auditKept: "The discard receipt stays after the section is removed.",
  check: "Check",
  offer: "Offer again",
  offerJob: "Confirm a new draft of this saved section in another open term.",
  notOffered: "Not offered again",
  offerDetail: "Create a new draft in another term, then review and publish it there.",
  openDraft: "Open new draft",
  term: "Term",
  offerTerm: "Term for the new draft",
  chooseTerm: "Choose a term",
  chooseOpenTerm: "Choose another open term for this section.",
  noOtherTerm: "There is no other open term. Create one here to continue.",
  offerCopied: "Copies the course definition, recurring schedule, capacity and active teaching team. Every carried teacher must still be assignable.",
  offerNotCopied: "No CRN, publication, enrollments, sessions, course plan, materials, coursework, Classroom or Drive mapping, meeting link, outcomes or certification is copied. The new course's teaching team chooses what to reuse and sets its dates for the new term.",
  existingSections: "Sections already in this term",
  existingWarning: "This term already holds this course code. Confirm again only to add a separate section.",
  addAnother: "Add another section",
  createDraft: "Create draft",
  whenOffer: "When you create the new draft",
  offerEffect: (code: string, term: string, duplicate: boolean) => `Creates ${duplicate ? "an additional" : "a"} draft section of ${code} in ${term}. The source section stays as it is.`,
  assignmentEnded: "Assignment ended",
  team: "Teaching team",
  teamJob: "Read the faculty assignments and confirm each change to teaching access in its row.",
  teamOnRecord: "Teaching assignments",
  teachersUnit: "teachers",
  primary: "Primary instructor",
  coInstructor: "Co-instructor",
  noTeachers: "No teachers assigned",
  noSignIn: "No sign-in yet",
  teachingAccess: "Active teaching access",
  noActiveAccess: "No active sign-in access",
  closedTerm: "Closed term",
  closedTeam: "A closed term's teaching team cannot change.",
  archiveTeam: "An archived section's teaching assignments remain on record and cannot change here.",
  changeTeaching: "Change assignment",
  teachingChange: "Teaching assignment",
  change: "Change",
  reason: "Reason",
  makePrimary: "Make primary",
  endAssignment: "End assignment",
  notChanged: "Not changed",
  endReason: "Name why this teaching assignment is ending.",
  whenTeachingChanges: "When you change this teaching assignment",
  primaryEffect: (teacher: string, course: string) => `Makes ${teacher} the primary instructor of ${course}. The previous primary assignment ends.`,
  endEffect: (teacher: string, course: string) => `Ends ${teacher}'s teaching assignment on ${course}. Other memberships and academic records stay.`,
  addEffect: (teacher: string, course: string, primary: boolean) => `Assigns ${teacher} to teach ${course}${primary ? " as primary instructor, ending the previous primary assignment" : " as co-instructor"}.`,
  membershipUnchanged: "This changes the course assignment, not the person's tenant membership. Sign-in and access remain separate.",
  newerTeam: "The section or its teaching team changed after this draft began. Compare the current assignments before continuing.",
  teamChoicesWait: "Read the section and faculty choices before changing its team.",
  saveFactsFirst: "Save or correct the unfinished section facts before confirming this change.",
  addTeacher: "Add teacher",
  addTeacherJob: "Choose an eligible faculty record and confirm its teaching assignment.",
  chooseFaculty: "Choose faculty",
  chooseEligibleFaculty: "Choose an eligible faculty record not already in this assignment.",
  facultyBoundary: "Choose a faculty record. A name or account never creates a teaching identity.",
  faculty: "Faculty record",
  assignment: "Assignment",
  notAdded: "Not added",
  section: "Section",
  openRoster: "Open roster",
  openSectionRecord: "Open section record",
  courseReadFailed: "This section could not be read. Try again.",
  courseReadStale: "Could not refresh. A later change is not shown.",
  sectionJob: "Read and correct one saved fact of this section at a time.",
  meets: "When and where it meets",
  meetsJob: "Keep the section's schedule and destination with their owners.",
  publicationPlacement: "Program and term are fixed once a section is published.",
  unassigned: "Unassigned",
  emptyValue: "Not recorded",
  choicesUnavailable: "The choices could not be read. Saved facts are kept. Try again before choosing.",
  choicesLoading: "Reading choices",
  registrarOwned: "Set by the registrar.",
  destinationOwner: "This destination is owned by its meeting provider. It establishes no IMAM attendance or academic record.",
  schedule: "Schedule",
  scheduleDays: "Meeting days",
  scheduleStart: "Starts",
  scheduleEnd: "Ends",
  scheduleZone: "Time zone",
  newerSection: "This section changed after these edits began. Choose the version to keep before saving.",
  keepMine: "Keep my edits",
  loadNewer: "Load newer version",
  coursework: "Coursework oversight",
  courseworkJob: "Read this section's coursework and the terms learners were given.",
  courseworkReady: "Assignments on record",
  courseworkUnavailable: "The coursework for this section could not be read.",
  courseworkStale: "Couldn't refresh. A later change isn't shown.",
  courseworkEmpty: "No assignments set",
  courseworkEmptyDetail: "The instructor has not set coursework in this section yet.",
  courseworkFind: "Find an assignment",
  courseworkAll: "All states",
  courseworkFilter: "Assignment state",
  courseworkHistory: "Read terms",
  termsUnavailable: "The assignment terms could not be read.",
  termsLoading: "Reading assignment terms",
  retry: "Try again",
  instructions: "Instructions",
  noInstructions: "No instructions recorded",
  due: "Due",
  points: "Points",
  published: "Published",
  closed: "Closed",
  notSet: "Not set",
  notPublished: "Not published",
  notClosed: "Not closed",
  assignmentsUnit: "Assignments",
  publishedUnit: "Published",
  draftUnit: "Draft",
  closedUnit: "Closed",
  counts: (handedIn: number, released: number) => `${handedIn} handed in · ${released} released`,
  readOnlyBoundary: "Administration reads how many learners handed work in and how many have a released result. Attempts and judgments belong to the teaching team. Certified outcomes are kept with grades and academic history.",
};
